// marginMarkersPlugin.js
// Compute Google-Docs-style margin markers for meta marks (comment/reference/task)

import {Plugin, PluginKey, TextSelection} from 'prosemirror-state';
import {Decoration, DecorationSet} from 'prosemirror-view';
import useReferenceNumberStore from '../stores/useReferenceNumberStore';
import {parseSuggestionAuthor} from '../core/createEditorView';

export const marginMarkersPluginKey = new PluginKey('marginMarkers');

const COLLAPSED_MARKER_HEADER_HEIGHT_PX = 40;

function sameActiveMarker(left, right) {
    return left?.type === right?.type && left?.uid === right?.uid;
}

function getMarkerSortTuple(marker) {
    return {
        from: marker?.from ?? -1,
        to: marker?.to ?? Number.POSITIVE_INFINITY,
    };
}

function getSelectionMetaCandidates(state, markersMap) {
    const {selection, schema, storedMarks} = state;
    if (!(selection instanceof TextSelection) || !selection.empty) {
        return {candidates: [], referenceCandidates: []};
    }

    const {$from} = selection;
    const marks = [
        ...(storedMarks || $from.marks()),
        ...($from.parentOffset > 0 && $from.nodeBefore?.marks ? $from.nodeBefore.marks : []),
        ...($from.nodeAfter?.marks || []),
    ];
    const seen = new Set();
    const candidates = [];
    const referenceCandidates = [];

    for (const mark of marks) {
        let type = null;
        if (mark.type === schema.marks.comment) type = 'comment';
        else if (mark.type === schema.marks.reference) type = 'reference';
        else if (mark.type === schema.marks.task) type = 'task';
        else if (schema.marks.insertion && mark.type === schema.marks.insertion) type = 'insertion';
        else if (schema.marks.deletion && mark.type === schema.marks.deletion) type = 'deletion';

        if (!type) continue;

        let uid;
        if (type === 'insertion' || type === 'deletion') {
            const id = String(mark.attrs.id);
            // A replacement creates both an insertion and deletion with the same id —
            // these are merged into a single 'change' entry in collectMetaMarkUids.
            // Prefer the merged key; fall back to the individual type key.
            const changeKey = `change:${id}`;
            if (markersMap.has(changeKey)) {
                uid = id;
                type = 'change';
            } else {
                uid = id;
            }
        } else {
            uid = mark.attrs.uid;
        }

        if (!uid) continue;

        const key = `${type}:${uid}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const marker = markersMap.get(key);
        if (!marker) continue;

        candidates.push(marker);
        if (type === 'reference') {
            referenceCandidates.push(marker);
        }
    }

    return {candidates, referenceCandidates};
}

function chooseInnermostMarker(markers = []) {
    if (!markers.length) return {type: null, uid: null};

    const [selected] = [...markers].sort((left, right) => {
        const leftTuple = getMarkerSortTuple(left);
        const rightTuple = getMarkerSortTuple(right);
        if (rightTuple.from !== leftTuple.from) {
            return rightTuple.from - leftTuple.from;
        }
        return leftTuple.to - rightTuple.to;
    });

    return {type: selected.type, uid: selected.uid};
}

function deriveSelectionMarkerState(state, markersMap) {
    const {candidates, referenceCandidates} = getSelectionMetaCandidates(state, markersMap);
    return {
        active: chooseInnermostMarker(candidates),
        activeReferenceUid: chooseInnermostMarker(referenceCandidates).uid ?? null,
    };
}

function resolveMarginRailScrollContainer(editorView) {
    return editorView.dom.closest('.pm-editor-scroll-container')
        || editorView.dom.parentElement
        || editorView.dom;
}

function resolveMarginRailPositionRoot(editorView) {
    // Resolve to the MarginRail container (.pm-margin-rail), which is the
    // position:relative parent that MarkerItem children anchor to.
    // Walk up from the editor to the scroll container, then search within it.
    const scrollContainer = resolveMarginRailScrollContainer(editorView);
    const rail = scrollContainer?.querySelector('.pm-margin-rail');
    if (rail) return rail;

    // Fallbacks for layouts without a margin rail or legacy class names.
    return editorView.dom.closest('.pm-editor-page-grid')
        || editorView.dom.closest('.pm-editor-content')
        || editorView.dom.parentElement
        || editorView.dom;
}

/**
 * Helper to get ranges for a meta mark without full editor state
 * @param {Object} params - Parameters
 * @param {Node} params.doc - ProseMirror document
 * @param {Schema} params.schema - ProseMirror schema
 * @param {string} markType - 'comment', 'reference', or 'task'
 * @param {string} uid - The UID to search for
 * @returns {Array<{from: number, to: number}>}
 */
function getMetaMarkRanges({doc, schema}, markType, uid) {
    const mark = schema.marks[markType];
    if (!mark) return [];

    const ranges = [];
    let currentRange = null;

    doc.descendants((node, pos) => {
        if (!node.isText) return;

        const hasMark = node.marks.some(
            (m) => m.type === mark && m.attrs.uid === uid
        );

        if (hasMark) {
            if (currentRange && currentRange.to === pos) {
                // Extend current range
                currentRange.to = pos + node.nodeSize;
            } else {
                // Start new range
                currentRange = {from: pos, to: pos + node.nodeSize};
                ranges.push(currentRange);
            }
        }
    });

    return ranges;
}

/**
 * Collects meta mark UIDs from the document in a single pass.
 * Text content for reference markers is gathered inline — no second scan.
 * @param {Node} doc - ProseMirror document node
 * @param {Schema} schema - ProseMirror schema
 * @param {Map} [prevByUid] - Previous markers map for object identity preservation
 * @returns {Array} Array of marker objects with stable identity
 */
function collectMetaMarkUids(doc, schema, prevByUid = null) {
    const byUid = new Map();
    // Accumulate reference text spans inline to avoid a second doc.descendants call.
    // ponytail: Map<key, string[]> — joined after the walk.
    const refTextByKey = new Map();

    doc.descendants((node, pos) => {
        if (!node.isText) return;

        for (const mark of node.marks) {
            let type = null;
            if (mark.type === schema.marks.comment) type = 'comment';
            else if (mark.type === schema.marks.reference) type = 'reference';
            else if (mark.type === schema.marks.task) type = 'task';
            else if (schema.marks.insertion && mark.type === schema.marks.insertion) type = 'insertion';
            else if (schema.marks.deletion && mark.type === schema.marks.deletion) type = 'deletion';

            if (!type) continue;

            let uid;
            if (type === 'insertion' || type === 'deletion') {
                // The new library assigns a stable numeric id per suggestion mark.
                // Each contiguous span of the same type+id is one logical suggestion,
                // so mark.attrs.id is the perfect natural uid — no run-tracking needed.
                uid = String(mark.attrs.id);
            } else {
                uid = mark.attrs.uid;
            }

            if (!uid) continue;

            const key = `${type}:${uid}`;
            const from = pos;
            const to = pos + node.nodeSize;

            if (!byUid.has(key)) {
                let markAttrs;
                if (type === 'insertion' || type === 'deletion') {
                    const {userId, isAI} = parseSuggestionAuthor(mark.attrs.id);
                    markAttrs = {...mark.attrs, userId, isAI};
                }
                byUid.set(key, {uid, type, from, to, markAttrs});
            } else {
                const entry = byUid.get(key);
                if (from < entry.from) entry.from = from;
                if (to > entry.to) entry.to = to;
            }

            // Collect reference text inline (avoids a second doc.descendants per reference)
            if (type === 'reference') {
                if (!refTextByKey.has(key)) refTextByKey.set(key, []);
                refTextByKey.get(key).push(node.text || '');
            }
        }
    });

    // Merge same-id insertion + deletion pairs into a single "change" entry so that
    // a replacement operation (select text → type new text) shows as one pill on the
    // margin rail instead of two separate pills.
    const seenChangeIds = new Set();
    const mergedByUid = new Map();

    for (const [key, entry] of byUid) {
        if (entry.type === 'insertion' || entry.type === 'deletion') {
            const partnType = entry.type === 'insertion' ? 'deletion' : 'insertion';
            const partnerKey = `${partnType}:${entry.uid}`;
            if (byUid.has(partnerKey) && !seenChangeIds.has(entry.uid)) {
                // Both insertion and deletion exist for this id → merge into one "change" pill.
                seenChangeIds.add(entry.uid);
                const partner = byUid.get(partnerKey);
                mergedByUid.set(`change:${entry.uid}`, {
                    uid: entry.uid,
                    type: 'change',
                    from: Math.min(entry.from, partner.from),
                    to: Math.max(entry.to, partner.to),
                    markAttrs: {
                        // Preserve the original id (may be a string like "userId:N" when user
                        // is logged in). Converting to Number would produce NaN for that format,
                        // causing applySuggestion/revertSuggestion to silently no-op.
                        id: entry.markAttrs?.id ?? partner.markAttrs?.id,
                        userId: entry.markAttrs?.userId ?? partner.markAttrs?.userId ?? null,
                        isAI: entry.markAttrs?.isAI ?? partner.markAttrs?.isAI ?? false,
                    },
                });
            } else if (!seenChangeIds.has(entry.uid) && !mergedByUid.has(key)) {
                // Standalone insertion or deletion — keep as-is.
                mergedByUid.set(key, entry);
            }
        } else {
            mergedByUid.set(key, entry);
        }
    }

    const markers = Array.from(mergedByUid.values());
    markers.forEach(marker => {
        if (marker.type === 'reference') {
            const key = `reference:${marker.uid}`;
            const parts = refTextByKey.get(key);
            if (parts && parts.length > 0) {
                const fullText = parts.join(' ').trim();
                marker.fullContent = fullText;
                marker.contentPreview = fullText.length > 32 ? fullText.substring(0, 32) + '...' : fullText;
            }
        }
    });

    return markers;
}

/**
 * Pack markers vertically to avoid overlap.
 * @param {Array} markers - Array of markers to pack
 * @param {Object} options - Packing options
 * @param {Map} [prevMarkersMap] - Previous markers for identity preservation
 * @returns {Array} Packed markers with stable object identity
 */
function packVertically(markers, {gapPx = 10, heightByType = {}, active = null, prevMarkersMap = null, containerHeightPx = null, iconMode = false} = {}) {
    const defaultHeight = 80; // estimated collapsed marker height
    let lastBottom = -Infinity;

    const packed = markers.map((m) => {
        const height = heightByType[m.type] ?? defaultHeight;
        const packedTopPx = Math.max(m.topPx, lastBottom + gapPx);
        lastBottom = packedTopPx + height;

        // Reuse previous object if coordinates match
        const key = `${m.type}:${m.uid}`;
        const prev = prevMarkersMap?.get(key);
        if (prev && prev.topPx === m.topPx && prev.packedTopPx === packedTopPx && prev.isVisible === m.isVisible) {
            return prev;
        }
        return {...m, packedTopPx};
    });

    // In icon mode nothing expands, so skip the active-marker shift-up entirely.
    if (!active || iconMode) return packed;

    const idx = packed.findIndex((m) => m.type === active.type && m.uid === active.uid);
    if (idx === -1) return packed;

    // Expanded reference panel: header ~40px + tabs ~48px + content maxHeight 240px + padding ~16px ≈ 344px.
    const EXPANDED_PANEL_HEIGHT_PX = 344;
    const BOTTOM_PADDING_PX = 16;

    const desiredShiftUp = packed[idx].packedTopPx - packed[idx].topPx;

    // Even when no packing shift is needed, the active marker may still overflow the viewport
    // bottom at its natural position. Compute a direct bottom clamp independently.
    let shift = desiredShiftUp > 0 ? desiredShiftUp : 0;

    if (containerHeightPx != null && containerHeightPx > 0) {
        const maxBottom = containerHeightPx - BOTTOM_PADDING_PX;
        const activePackedTop = packed[idx].packedTopPx - shift;
        const activeBottom = activePackedTop + EXPANDED_PANEL_HEIGHT_PX;
        if (activeBottom > maxBottom) {
            const overflow = activeBottom - maxBottom;
            // If we were going to shift up, reduce the shift; otherwise shift up to bring it in view.
            shift = Math.max(0, shift - overflow);
            if (desiredShiftUp <= 0) {
                // No pack shift — directly clamp the active marker upward
                shift = overflow;
            }
        }
    }

    if (shift === 0) return packed;

    return packed.map((m) => {
        const newPackedTopPx = m.packedTopPx - shift;
        const key = `${m.type}:${m.uid}`;
        const prev = prevMarkersMap?.get(key);
        if (prev && prev.topPx === m.topPx && prev.packedTopPx === newPackedTopPx && prev.isVisible === m.isVisible) {
            return prev;
        }
        return {...m, packedTopPx: newPackedTopPx};
    });
}

export function marginMarkersPlugin(options = {}) {
    const {minGapPx = 0, collapsedHeightByType = {comment: 44, reference: 60, task: 44, insertion: 44, deletion: 44, change: 44}, reportId = null} = options;

    return new Plugin({
        key: marginMarkersPluginKey,

        props: {
            decorations(state) {
                const pluginState = marginMarkersPluginKey.getState(state);
                if (!pluginState) return DecorationSet.empty;

                const {active, markersMap} = pluginState;
                if (!active?.type || !active?.uid) return DecorationSet.empty;

                // Only decorate suggestion-type markers — comment/reference/task already
                // get their active highlight via metaMarksPlugin's CSS class toggle.
                const isSuggestion = active.type === 'insertion' || active.type === 'deletion' || active.type === 'change';
                if (!isSuggestion) return DecorationSet.empty;

                const key = `${active.type}:${active.uid}`;
                const marker = markersMap.get(key);
                if (!marker || marker.markAttrs?.id == null) return DecorationSet.empty;

                // Walk the document and add one inline decoration per text node that
                // carries a matching insertion or deletion mark. The class lands on an
                // inner <span> inside the <ins>/<del> element; the CSS uses the
                // descendant selector `ins[data-id] .pm-suggestion-mark--active`.
                const id = marker.markAttrs.id;
                const decos = [];

                state.doc.descendants((node, pos) => {
                    if (!node.isText) return;
                    const hasMark = node.marks.some(
                        (m) => (
                            m.type === state.schema.marks.insertion ||
                            m.type === state.schema.marks.deletion
                        ) && m.attrs.id === id
                    );
                    if (hasMark) {
                        decos.push(Decoration.inline(pos, pos + node.nodeSize, {
                            class: 'pm-suggestion-mark--active',
                        }));
                    }
                });

                return DecorationSet.create(state.doc, decos);
            },
        },

        state: {
            init(_, state) {
                const base = collectMetaMarkUids(state.doc, state.schema);
                const markers = base.map((m) => ({
                    ...m,
                    topPx: null,
                    packedTopPx: null,
                    isVisible: false,
                }));
                const markersMap = new Map();
                markers.forEach(m => markersMap.set(`${m.type}:${m.uid}`, m));
                const selectionState = deriveSelectionMarkerState(state, markersMap);

                return {
                    markers,
                    markersMap,
                    active: selectionState.active,
                    activeReferenceUid: selectionState.activeReferenceUid,
                    version: 0, // Increment when markers/active change
                };
            },
            apply(tr, pluginState, _oldState, newState) {
                let next = pluginState;
                let changed = false;

                const meta = tr.getMeta(marginMarkersPluginKey);
                if (meta?.type === 'setActive') {
                    // Only update if active actually changed
                    const activeChanged = !sameActiveMarker(pluginState.active, meta.active);
                    if (activeChanged) {
                        next = {...next, active: meta.active, version: next.version + 1};
                        changed = true;
                    }
                }

                // Only recollect markers when document changes
                if (tr.docChanged) {
                    const base = collectMetaMarkUids(newState.doc, newState.schema, pluginState.markersMap);
                    const baseMap = new Map();

                    // Preserve objects where possible
                    base.forEach(m => {
                        const key = `${m.type}:${m.uid}`;
                        const prev = pluginState.markersMap.get(key);

                        // If marker exists and 'from' position and fullContent unchanged, reuse it
                        if (prev && prev.from === m.from && prev.fullContent === m.fullContent) {
                            baseMap.set(key, prev);
                        } else {
                            // Position changed or new marker or fullContent changed
                            const marker = {...m, topPx: null, packedTopPx: null, isVisible: false};
                            baseMap.set(key, marker);
                        }
                    });

                    const baseMarkers = Array.from(baseMap.values());

                    // Only update if markers actually changed
                    if (baseMarkers.length !== pluginState.markers.length || changed) {
                        next = {...next, markers: baseMarkers, markersMap: baseMap, version: next.version + 1};
                    } else {
                        // Check if any marker changed
                        let anyChanged = false;
                        for (let i = 0; i < baseMarkers.length; i++) {
                            if (baseMarkers[i] !== pluginState.markers[i]) {
                                anyChanged = true;
                                break;
                            }
                        }
                        if (anyChanged) {
                            next = {...next, markers: baseMarkers, markersMap: baseMap, version: next.version + 1};
                        } else if (changed) {
                            next = {...next, markers: pluginState.markers, markersMap: baseMap};
                        }
                    }

                    // Sync reference order with the store (ProseMirror best practice: don't do side effects in apply)
                    // Instead, we'll do this in the view's update method
                    if (reportId && baseMarkers.length > 0) {
                        // Mark that we need to sync references
                        next._needsReferenceSync = true;
                        next._referenceMarkers = baseMarkers.filter(m => m.type === 'reference');
                    }
                }

                const selectionChanged = !_oldState.selection.eq(newState.selection);
                if (tr.docChanged || selectionChanged) {
                    const selectionState = deriveSelectionMarkerState(newState, next.markersMap);
                    const activeChanged = !sameActiveMarker(next.active, selectionState.active);
                    const activeReferenceChanged = next.activeReferenceUid !== selectionState.activeReferenceUid;

                    if (activeChanged || activeReferenceChanged) {
                        next = {
                            ...next,
                            active: selectionState.active,
                            activeReferenceUid: selectionState.activeReferenceUid,
                            version: next.version + 1,
                        };
                    }
                }

                return next;
            },
        },

        view(editorView) {
            let raf = null;
            let lastVersion = -1;
            let lastScrollTop = -1;
            let cachedMarkers = null;
            const viewMarkerCache = new Map();

            // Store markers on the view itself (not in plugin state)
            const viewMarkers = {
                markers: [],
                active: null,
                activeReferenceUid: null,
                listeners: new Set(),
            };

            // Allow React to subscribe
            editorView.__marginMarkersView = viewMarkers;

            const computeCoords = () => {
                const pluginState = marginMarkersPluginKey.getState(editorView.state);
                if (!pluginState) return null;

                const positionRoot = resolveMarginRailPositionRoot(editorView);
                const positionRootRect = positionRoot.getBoundingClientRect();
                const positionRootTop = positionRootRect.top;
                // Visible bottom of the position root in packedTopPx-space (viewport-relative coords).
                // Use window.innerHeight as the viewport bottom bound so the panel never overflows
                // below the visible screen regardless of how tall the document is.
                const visibleBottomPx = Math.min(positionRootRect.bottom, window.innerHeight) - positionRootTop;
                const {active} = pluginState;
                // Read the rail mode set by MarginRail via data-rail-mode attribute.
                const iconMode = positionRoot?.dataset?.railMode === 'icon';

                const withCoords = pluginState.markers
                    .map((m) => {
                        try {
                            const coords = editorView.coordsAtPos(m.from);
                            const lineCenterPx = ((coords.top + coords.bottom) / 2) - positionRootTop;
                            const topPx = lineCenterPx - (COLLAPSED_MARKER_HEADER_HEIGHT_PX / 2);
                            const isVisible = Number.isFinite(topPx);

                            const key = `${m.type}:${m.uid}`;
                            const prev = viewMarkerCache.get(key);

                            // Reuse if coordinates match
                            if (prev && prev.topPx === topPx && prev.isVisible === isVisible) {
                                return prev;
                            }
                            return {...m, topPx, isVisible, packedTopPx: topPx};
                        } catch {
                            const key = `${m.type}:${m.uid}`;
                            const prev = viewMarkerCache.get(key);
                            if (prev && prev.topPx === null && prev.isVisible === false) {
                                return prev;
                            }
                            return {...m, topPx: null, packedTopPx: null, isVisible: false};
                        }
                    })
                    .filter((m) => m.isVisible)
                    .sort((a, b) => a.topPx - b.topPx);

                // Pack vertically
                const packed = packVertically(withCoords, {
                    gapPx: minGapPx,
                    heightByType: collapsedHeightByType,
                    active,
                    prevMarkersMap: viewMarkerCache,
                    containerHeightPx: visibleBottomPx > 0 ? visibleBottomPx : null,
                    iconMode,
                });

                viewMarkerCache.clear();
                packed.forEach((marker) => {
                    viewMarkerCache.set(`${marker.type}:${marker.uid}`, marker);
                });

                return {
                    markers: packed,
                    active,
                    activeReferenceUid: pluginState.activeReferenceUid,
                };
            };

            const updateCoords = () => {
                raf = null;

                const scrollContainer = resolveMarginRailScrollContainer(editorView);
                const scrollTop = scrollContainer.scrollTop || 0;
                const pluginState = marginMarkersPluginKey.getState(editorView.state);

                // Check if we need to recompute
                const versionChanged = pluginState?.version !== lastVersion;
                const scrollChanged = Math.abs(scrollTop - lastScrollTop) > 1;

                if (!versionChanged && !scrollChanged && cachedMarkers) {
                    return; // No change needed
                }

                const result = computeCoords();
                if (!result) return;

                lastVersion = pluginState?.version ?? -1;
                lastScrollTop = scrollTop;

                // Check if markers actually changed
                const markersChanged = !cachedMarkers ||
                    cachedMarkers.markers.length !== result.markers.length ||
                    cachedMarkers.markers.some((m, i) => m !== result.markers[i]);

                const activeChanged = !sameActiveMarker(cachedMarkers?.active, result.active);
                const activeReferenceChanged = cachedMarkers?.activeReferenceUid !== result.activeReferenceUid;

                if (markersChanged || activeChanged || activeReferenceChanged) {
                    cachedMarkers = result;
                    viewMarkers.markers = result.markers;
                    viewMarkers.active = result.active;
                    viewMarkers.activeReferenceUid = result.activeReferenceUid;

                    // Notify React listeners
                    viewMarkers.listeners.forEach(fn => fn());
                }
            };

            const schedule = () => {
                if (raf != null) return;
                raf = window.requestAnimationFrame(updateCoords);
            };

            const onScroll = () => schedule();
            const onResize = () => schedule();

            const scrollContainer = resolveMarginRailScrollContainer(editorView);
            scrollContainer?.addEventListener('scroll', onScroll, {passive: true});
            window.addEventListener('resize', onResize);

            // Initialize reference order on mount
            if (reportId) {
                const pluginState = marginMarkersPluginKey.getState(editorView.state);
                if (pluginState) {
                    const referenceMarkers = pluginState.markers.filter(m => m.type === 'reference');
                    if (referenceMarkers.length > 0) {
                        const orderedRefs = referenceMarkers.map(m => ({uid: m.uid, from: m.from}));
                        const store = useReferenceNumberStore.getState();
                        store.updateReferenceOrder(reportId, orderedRefs);
                    }
                }
            }

            schedule();

            return {
                update(view, prevState) {
                    if (!prevState) {
                        schedule();
                        return;
                    }

                    const prevPlugin = marginMarkersPluginKey.getState(prevState);
                    const nextPlugin = marginMarkersPluginKey.getState(view.state);

                    // Sync reference order with store (side effect in view, not in apply)
                    if (reportId && nextPlugin?._needsReferenceSync && nextPlugin?._referenceMarkers) {
                        const referenceMarkers = nextPlugin._referenceMarkers;
                        const orderedRefs = referenceMarkers.map(m => ({uid: m.uid, from: m.from}));

                        // Update the store (this is safe in the view's update method)
                        const store = useReferenceNumberStore.getState();
                        store.updateReferenceOrder(reportId, orderedRefs);

                        // Clear the sync flag
                        delete nextPlugin._needsReferenceSync;
                        delete nextPlugin._referenceMarkers;
                    }

                    // Only schedule if version changed (document or active changed)
                    if (prevPlugin?.version !== nextPlugin?.version) {
                        schedule();
                    }
                },
                destroy() {
                    if (raf != null) window.cancelAnimationFrame(raf);
                    scrollContainer?.removeEventListener('scroll', onScroll);
                    window.removeEventListener('resize', onResize);
                    delete editorView.__marginMarkersView;
                },
            };
        },
    });
}

export function getMarginMarkersPluginState(state) {
    return marginMarkersPluginKey.getState(state);
}

export function getDerivedSelectionMarkerState(state) {
    const pluginState = marginMarkersPluginKey.getState(state);
    const markersMap = pluginState?.markersMap;
    if (!markersMap) {
        return {active: {type: null, uid: null}, activeReferenceUid: null};
    }
    return deriveSelectionMarkerState(state, markersMap);
}

export function setActiveMarginMarker(view, active) {
    const tr = view.state.tr.setMeta(marginMarkersPluginKey, {type: 'setActive', active});
    tr.setMeta('addToHistory', false);
    view.dispatch(tr);
}

export function selectMetaMarkThread(view, {type, uid}) {
    const ranges = getMetaMarkRanges(view.state, type, uid);
    if (!ranges.length) return false;

    const {from, to} = ranges[0];
    const tr = view.state.tr.setSelection(TextSelection.create(view.state.doc, from, to));
    view.dispatch(tr);
    view.focus();
    return true;
}

export function focusMetaMarkStart(view, {type, uid}) {
    const ranges = getMetaMarkRanges(view.state, type, uid);
    if (!ranges.length) return false;

    const {from} = ranges[0];
    const tr = view.state.tr.setSelection(TextSelection.create(view.state.doc, from));
    view.dispatch(tr);
    view.focus();
    return true;
}
